import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export function isSentinelAllValue(id) {
  if (id == null) return true;
  const value = String(id).trim().toLowerCase();
  return value === "" || value === "all";
}

const FilterDropdown = ({
  orderStatuses,
  activeTab,
  setActiveTab,
  className,
  showLabel = true,
  labelKey = "status",
  label,
  onSelectItem,
  shouldCloseOnSelect,
  renderExtraMenu,
  hideIcons = false,
}) => {
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const filterDropdownRef = useRef(null);
  const itemRefs = useRef([]);
  const { t } = useTranslation("translation");

  const allOption = useMemo(
    () => (orderStatuses || []).find((status) => isSentinelAllValue(status?.id)),
    [orderStatuses]
  );
  const menuOptions = useMemo(
    () => (orderStatuses || []).filter((status) => !isSentinelAllValue(status?.id)),
    [orderStatuses]
  );
  const defaultId = allOption ? allOption.id : "";
  const isDefault = isSentinelAllValue(activeTab);
  const selectedName =
    (orderStatuses || []).find((status) => status.id === activeTab)?.name ||
    allOption?.name ||
    t("all", "All");

  const applyValue = (id) => {
    if (onSelectItem) {
      onSelectItem(id);
    } else {
      setActiveTab(id);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setShowFilterDropdown(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const scrollToItem = (index) => {
    const el = itemRefs.current[index];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: "nearest" });
    }
  };

  const handleKeyDown = (e) => {
    if (!showFilterDropdown || menuOptions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = (highlightedIndex + 1) % menuOptions.length;
      setHighlightedIndex(next);
      scrollToItem(next);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const prev =
        (highlightedIndex - 1 + menuOptions.length) % menuOptions.length;
      setHighlightedIndex(prev);
      scrollToItem(prev);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < menuOptions.length) {
        const status = menuOptions[highlightedIndex];
        applyValue(status.id);
        const shouldClose = shouldCloseOnSelect
          ? shouldCloseOnSelect(status.id)
          : true;
        if (shouldClose) {
          setShowFilterDropdown(false);
          setHighlightedIndex(-1);
        }
      }
    }
  };

  return (
    <div
      className={`custom-filter-wrapper oh-filter-field ${className || ""}`}
      ref={filterDropdownRef}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {showLabel && (
        <label htmlFor="filter-status" className="oh-field__label custom-filter-label">
          {!hideIcons && (
            <i className="bi bi-funnel oh-filter-bar__icon" aria-hidden="true" />
          )}
          {label || t(labelKey)}
        </label>
      )}

      <button
        type="button"
        className="custom-filter-button"
        onClick={() => {
          setShowFilterDropdown((prev) => {
            const next = !prev;
            if (next) setHighlightedIndex(0);
            else setHighlightedIndex(-1);
            return next;
          });
        }}
        title={selectedName}
      >
        <span className="custom-filter-button-text">{selectedName}</span>
        {!isDefault && (
          <span
            className="custom-filter-clear"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              applyValue(defaultId);
              setShowFilterDropdown(false);
              setHighlightedIndex(-1);
            }}
            role="button"
            tabIndex={0}
            aria-label={t("clear", { defaultValue: "Clear" })}
          >
            ✕
          </span>
        )}
        <i
          className={`bi bi-chevron-down toolbar-dropdown-icon ${
            showFilterDropdown ? "toolbar-dropdown-icon-rotate" : ""
          }`}
        />
      </button>

      {showFilterDropdown && (
        <div className="custom-filter-menu">
          {menuOptions.map((status, index) => (
            <div
              key={status.id}
              ref={(el) => (itemRefs.current[index] = el)}
              className={`custom-dropdown-item${
                activeTab === status.id ? " active" : ""
              }${highlightedIndex === index ? " highlighted" : ""}`}
              onMouseEnter={() => setHighlightedIndex(index)}
              onMouseLeave={() => setHighlightedIndex(-1)}
              onClick={() => {
                applyValue(status.id);
                const shouldClose = shouldCloseOnSelect
                  ? shouldCloseOnSelect(status.id)
                  : true;
                if (shouldClose) {
                  setShowFilterDropdown(false);
                  setHighlightedIndex(-1);
                }
              }}
            >
              {status.name}
            </div>
          ))}
          {renderExtraMenu &&
            renderExtraMenu(() => setShowFilterDropdown(false))}
        </div>
      )}
    </div>
  );
};

export default FilterDropdown;
